import { PurchaseDocFileState, PurchaseDocSignature } from "types/InvoiceInbox";

/**
 * The signing state of one file of a purchase document, read from the
 * detail's `signatures` + `fileStates`. Shared by the file chips, the
 * "Signatures" section and the request dialog so all three agree on what
 * "signed" and "in progress" mean.
 */

export const PRIMARY_FILE_KEY = "primary";

export type FileSignState = "signed" | "pending" | "rejected" | "none";

export function signaturesFor(signatures: PurchaseDocSignature[] | undefined, fileKey: string): PurchaseDocSignature[] {
  return (signatures || []).filter((row) => row.fileKey === fileKey);
}

/** A signature still waiting on this very user — what the "Sign" link is for. */
export function isPendingSignatureOf(row: PurchaseDocSignature, userId: string | null): boolean {
  return row.status === "pending" && row.ref === "Users" && !!userId && row.signerId === userId;
}

/** Signed wins (the file IS signed, whatever an old row says); then a request
 * still waiting on somebody; then a request somebody declined. */
export function fileSignState(
  fileKey: string,
  signatures: PurchaseDocSignature[] | undefined,
  fileStates: Record<string, PurchaseDocFileState> | undefined
): FileSignState {
  if (fileStates && fileStates[fileKey] && fileStates[fileKey].isSigned) return "signed";
  const rows = signaturesFor(signatures, fileKey);
  if (rows.some((row) => row.status === "pending")) return "pending";
  if (rows.some((row) => row.status === "rejected")) return "rejected";
  return "none";
}

/** A PDF by type, OR by name — same rule the server applies. A mime type of
 * "application/pdf; charset=binary" (params stripped, compared
 * case-insensitively) counts, and so does a `.pdf` file name even when the
 * recorded mime type says something else (an upload mis-typed by the
 * browser, or a mail client's generic "application/octet-stream"). Only a
 * PDF can be sent for an e-signature. */
export function isPdfFile(mimeType: string | null | undefined, fileName: string | null | undefined): boolean {
  const normalizedMime = (mimeType || "").split(";")[0].trim().toLowerCase();
  if (normalizedMime === "application/pdf") return true;
  return Boolean(fileName && /\.pdf$/i.test(fileName));
}
